import { useHandler } from "@redotech/react-util/hook";
import CheckCircleIcon from "@redotech/redo-icons/check-circle.svg";
import XCircleIcon from "@redotech/redo-icons/x-circle.svg";
import { StorefrontFeatures } from "@redotech/redo-model/split";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useState } from "react";
import { CartProgressBarConfigModal } from "./cart-progress-bar-config-modal";
import { TextEditsConfigModal } from "./text-edits-config-modal";
import * as css from "./treatment-storefront-features-configuration.module.css";

type TreatmentStorefrontFeaturesConfigurationProps = (
  | { readonly: true }
  | {
      readonly: false;
      onStorefrontFeaturesChange: (
        treatmentId: string,
        storefrontFeatures: StorefrontFeatures,
      ) => void;
    }
) & {
  treatmentId: string;
  treatmentName: string;
  storefrontFeatures?: StorefrontFeatures;
};

export const TreatmentStorefrontFeaturesConfiguration = memo(
  function TreatmentStorefrontFeaturesConfiguration({
    treatmentId,
    treatmentName,
    storefrontFeatures,
    ...rest
  }: TreatmentStorefrontFeaturesConfigurationProps) {
    const [showProgressBarModal, setShowProgressBarModal] = useState(false);
    const [showTextEditsModal, setShowTextEditsModal] = useState(false);

    const handleProgressBarChange = useHandler(
      (progressBar: StorefrontFeatures["progressBar"]) => {
        if (rest.readonly) {
          return;
        }

        rest.onStorefrontFeaturesChange(treatmentId, {
          ...storefrontFeatures,
          progressBar,
          textEdits: storefrontFeatures?.textEdits ?? [],
        });
        setShowProgressBarModal(false);
      },
    );

    const handleTextEditsChange = useHandler(
      (textEdits: StorefrontFeatures["textEdits"]) => {
        if (rest.readonly) {
          return;
        }

        rest.onStorefrontFeaturesChange(treatmentId, {
          ...storefrontFeatures,
          textEdits,
          progressBar: storefrontFeatures?.progressBar,
        });
        setShowTextEditsModal(false);
      },
    );

    return (
      <Flex dir="column" gap="xs" w="full">
        <Text fontSize="xs" fontWeight="medium">
          {treatmentName}
        </Text>
        <Flex
          borderColor="primary"
          borderStyle="solid"
          borderWidth="1px"
          dir="column"
          gap="3xl"
          p="xl"
          radius="xs"
          w="full"
        >
          <Flex dir="column" gap="md" w="full">
            <Text fontSize="xs" fontWeight="medium">
              Cart widget
            </Text>
            {storefrontFeatures?.progressBar ? (
              <Flex align="center" dir="row" gap="xs">
                <CheckCircleIcon className={css.icon} />
                <Text fontSize="xs" textColor="secondary">
                  Cart progress bar enabled
                </Text>
              </Flex>
            ) : rest.readonly ? (
              <Flex align="center" dir="row" gap="xs">
                <XCircleIcon className={css.icon} />
                <Text fontSize="xs" textColor="secondary">
                  No cart progress bar configured
                </Text>
              </Flex>
            ) : null}
            {!rest.readonly && (
              <RedoButton
                hierarchy="secondary"
                onClick={() => setShowProgressBarModal(true)}
                size="sm"
                text="Configure cart progress bar"
              />
            )}
          </Flex>
          <Flex dir="column" gap="md" w="full">
            <Text fontSize="xs" fontWeight="medium">
              Content edits
            </Text>
            {!!storefrontFeatures?.textEdits &&
            storefrontFeatures.textEdits.length > 0 ? (
              <Flex align="center" dir="row" gap="xs">
                <CheckCircleIcon className={css.icon} />
                <Text fontSize="xs" textColor="secondary">
                  {storefrontFeatures.textEdits.length} selector
                  {storefrontFeatures.textEdits.length > 1 ? "s" : ""}{" "}
                  configured
                </Text>
              </Flex>
            ) : rest.readonly ? (
              <Flex align="center" dir="row" gap="xs">
                <XCircleIcon className={css.icon} />
                <Text fontSize="xs" textColor="secondary">
                  No content edits configured
                </Text>
              </Flex>
            ) : null}
            {!rest.readonly && (
              <RedoButton
                hierarchy="secondary"
                onClick={() => setShowTextEditsModal(true)}
                size="sm"
                text="Configure content edits"
              />
            )}
          </Flex>
        </Flex>

        {showProgressBarModal && !rest.readonly && (
          <CartProgressBarConfigModal
            onClose={() => setShowProgressBarModal(false)}
            onSave={handleProgressBarChange}
            progressBar={storefrontFeatures?.progressBar ?? undefined}
            treatmentName={treatmentName}
          />
        )}

        {showTextEditsModal && !rest.readonly && (
          <TextEditsConfigModal
            onClose={() => setShowTextEditsModal(false)}
            onSave={handleTextEditsChange}
            textEdits={storefrontFeatures?.textEdits ?? []}
            treatmentName={treatmentName}
          />
        )}
      </Flex>
    );
  },
);
